import { useState } from 'react';

/**
 * SkipLink Component
 * 
 * Provides a "Skip to main content" link for keyboard users.
 * Hidden by default, visible on focus.
 * 
 * @example
 * <SkipLink targetId="main-content" />
 * <main id="main-content">...</main>
 */
export interface SkipLinkProps {
  /** The ID of the main content element to skip to */
  targetId?: string;
  /** Custom text for the link (default: German) */
  text?: string;
  /** Additional CSS classes */
  className?: string;
}

export function SkipLink({
  targetId = 'main-content',
  text = 'Zum Hauptinhalt springen',
  className = '',
}: SkipLinkProps) {
  const [isVisible, setIsVisible] = useState(false);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const target = document.getElementById(targetId);
    if (target) {
      // Set tabindex to allow programmatic focus
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: false });
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      
      // Remove tabindex after focus to maintain natural tab order
      setTimeout(() => {
        target.removeAttribute('tabindex');
      }, 1000);
    }
  };

  return (
    <a
      href={`#${targetId}`}
      onClick={handleClick}
      onFocus={() => setIsVisible(true)}
      onBlur={() => setIsVisible(false)}
      className={`
        fixed top-4 left-4 z-50
        px-4 py-3
        bg-blue-600 text-white
        font-medium text-base
        rounded-md shadow-lg
        transition-all duration-200
        focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-slate-900
        ${isVisible 
          ? 'translate-y-0 opacity-100' 
          : '-translate-y-full opacity-0 pointer-events-none'
        }
        ${className}
      `}
      aria-label={text}
    >
      {text}
    </a>
  );
}

export default SkipLink;
